<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import SchoolCreateForm, {
  type SchoolCreateFormData,
} from '@/components/admin/SchoolCreateForm.vue'
import { useEnvironment } from '@/composables/useEnvironment'
import { useCreateSchool } from '@/composables/api/useSchoolsApi'
import type { ServiceType } from '@/constants/environments'

const router = useRouter()
const { currentService } = useEnvironment()

const { mutateAsync, isPending, error } = useCreateSchool(
  currentService.value.key as ServiceType,
)

const errorMessage = computed(() => error.value?.message ?? null)

async function handleSubmit(data: SchoolCreateFormData) {
  try {
    await mutateAsync({
      name: data.name,
      full_name: data.full_name,
      is_production: data.is_production,
      use_sections: data.use_sections,
      admin: {
        email: data.admin_email,
        password: data.admin_password,
        first_name: data.admin_first_name,
        last_name: data.admin_last_name,
      },
    })
    router.push({ name: 'schools' })
  } catch {
    // Error already surfaced via toast inside the composable.
  }
}

function handleCancel() {
  router.push({ name: 'schools' })
}
</script>

<template>
  <AdminLayout>
    <div class="school-create">
      <div class="school-create__header">
        <h2 class="school-create__heading">Create School</h2>
      </div>
      <SchoolCreateForm
        :is-pending="isPending"
        :error="errorMessage"
        @submit="handleSubmit"
        @cancel="handleCancel"
      />
    </div>
  </AdminLayout>
</template>

<style scoped>
.school-create {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
}

.school-create__heading {
  font-size: 20px;
  font-weight: 600;
  color: #1c1917;
  margin: 0;
}
</style>
